/**
 * /portal/org-chart -- who works here, who they report to, and who to ask.
 *
 * One source: docs/org/org-chart.json, served by GET /api/v1/org-chart with
 * the server's roster cross-check. This page shows no resident or patient
 * data. It reads the chart; it changes nothing.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { StateBlock } from '../../components/tide';
import { OrgChartResponse, orgChartApi, Tier } from '../../services/orgChartApi';
import OrgTree, { ConfirmMarker, TierBadge } from './OrgTree';
import WhoToAsk from './WhoToAsk';
import { TIER_ORDER, byId, childrenOf, displayName, managersOf, matchesPosition, visibleFor } from './orgModel';
import './orgChart.css';

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; status?: number; message?: string }
  | { kind: 'ready'; data: OrgChartResponse };

/** The root and the level under it start open: the leadership is visible, every team one click away. */
function defaultExpanded(data: OrgChartResponse): Set<string> {
  const kids = childrenOf(data.chart);
  const open = new Set<string>();
  for (const root of kids.get(null) || []) {
    open.add(root.id);
    for (const c of kids.get(root.id) || []) open.add(c.id);
  }
  return open;
}

export default function OrgChartPage() {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [focusId, setFocusId] = useState<string | null>(null);

  const fetchChart = useCallback(() => {
    setLoad({ kind: 'loading' });
    orgChartApi.get()
      .then((r) => {
        if (r && r.success && r.data) {
          setLoad({ kind: 'ready', data: r.data });
          setExpanded(defaultExpanded(r.data));
        } else {
          setLoad({ kind: 'error', status: r ? r.status : undefined, message: r ? r.message : undefined });
        }
      })
      .catch(() => setLoad({ kind: 'error' }));
  }, []);

  useEffect(() => { fetchChart(); }, [fetchChart]);

  const data = load.kind === 'ready' ? load.data : null;
  const visible = useMemo(() => (data ? visibleFor(data.chart, query) : null), [data, query]);
  const hits = useMemo(
    () => new Set(data && query.trim() ? data.chart.positions.filter((p) => matchesPosition(p, query)).map((p) => p.id) : []),
    [data, query],
  );
  // While searching, every level on a match's path is open.
  const effectiveExpanded = visible || expanded;

  useEffect(() => {
    if (!focusId) return;
    const el = document.getElementById(`oc-pos-${focusId}`);
    if (el) {
      el.focus();
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
    }
    setFocusId(null);
  }, [focusId, effectiveExpanded]);

  const toggle = (id: string) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const showOnChart = (id: string) => {
    if (!data) return;
    setQuery('');
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const m of managersOf(data.chart, id)) next.add(m.id);
      return next;
    });
    setFocusId(id);
  };

  return (
    <>
      <WorkspaceMenu />
      <main className="oc" aria-labelledby="oc-title">
        <header className="oc-band">
          <h1 id="oc-title" className="oc-h1">Org chart</h1>
          <p className="oc-band__sub">Who works here, who they report to, and who to ask about what.</p>
        </header>

        {load.kind === 'loading' && <StateBlock kind="loading" shape="lines" label="Loading the org chart" />}
        {load.kind === 'error' && (
          <StateBlock
            kind="error"
            status={load.status}
            message={load.status === 403 ? (load.message || 'The org chart is open to the eMerge workforce.') : load.message}
            onRetry={fetchChart}
          />
        )}

        {data && (
          <>
            <section className="oc-key" aria-labelledby="oc-key-title">
              <h2 id="oc-key-title" className="oc-h2">Key</h2>
              <ul className="oc-key__list">
                {TIER_ORDER.map((t: Tier) => (
                  <li key={t} className="oc-key__item">
                    <TierBadge tier={t} label={`${data.chart.tiers[t].label} (${data.chart.tiers[t].chartColour})`} />
                  </li>
                ))}
                <li className="oc-key__item"><ConfirmMarker /> <span className="oc-key__desc">inferred from the chart or the roster; not yet confirmed by the person who owns it</span></li>
              </ul>
              <p className="oc-key__source">
                Transcribed {data.chart.source.transcribedOn}. {data.chart.source.authority}
              </p>
            </section>

            <div className="oc-search">
              <label htmlFor="oc-q" className="oc-search__label">Find a person, title or topic</label>
              <input
                id="oc-q"
                type="search"
                className="oc-search__input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. UMED, scheduling, Josie"
                autoComplete="off"
              />
              <p className="oc-search__count" role="status" aria-live="polite">
                {query.trim() ? `${hits.size} ${hits.size === 1 ? 'person matches' : 'people match'}` : ''}
              </p>
              {!query.trim() && (
                <div className="oc-search__tools">
                  <button type="button" className="oc-btn" onClick={() => setExpanded(new Set(data.chart.positions.map((p) => p.id)))}>Expand all</button>
                  <button type="button" className="oc-btn" onClick={() => setExpanded(new Set())}>Collapse all</button>
                </div>
              )}
            </div>

            <div className="oc-grid">
              <section className="oc-chart" aria-labelledby="oc-chart-title">
                <h2 id="oc-chart-title" className="oc-h2">Reporting lines</h2>
                {query.trim() && hits.size === 0
                  ? <p className="oc-empty">Nobody on the chart matches “{query}”.</p>
                  : (
                    <OrgTree
                      chart={data.chart}
                      roster={data.roster}
                      visible={visible}
                      hits={hits}
                      expanded={effectiveExpanded}
                      onToggle={toggle}
                    />
                  )}
              </section>
              <WhoToAsk chart={data.chart} query={query} onShow={showOnChart} />
            </div>

            <section className="oc-more" aria-label="What is still open">
              <details className="oc-details">
                <summary>Open questions ({data.chart.openQuestions.length}) and how the chart was read</summary>
                <h3 className="oc-h3">Assumptions</h3>
                <ul>{data.chart.assumptions.map((a) => <li key={a.id}><strong>{a.id}.</strong> {a.text}</li>)}</ul>
                <h3 className="oc-h3">Where the chart is ambiguous</h3>
                <ul>{data.chart.ambiguities.map((a) => <li key={a.id}><strong>{a.id}.</strong> {a.text}</li>)}</ul>
                <h3 className="oc-h3">Questions we will ask</h3>
                <ul>
                  {data.chart.openQuestions.map((q) => {
                    const who = byId(data.chart).get(q.askWho);
                    return <li key={q.id}><strong>{q.id}. {q.topic}</strong> (to {who ? displayName(who) : q.askWho}): {q.question}</li>;
                  })}
                </ul>
              </details>
              {!data.roster.available && (
                <p className="oc-note" role="note">The roster cross-check could not be read just now; the chart itself is current.</p>
              )}
              {data.roster.available && data.roster.notOnChart.length > 0 && (
                <details className="oc-details">
                  <summary>In the eMerge roster but not on the chart ({data.roster.notOnChart.length})</summary>
                  <ul>
                    {data.roster.notOnChart.map((r) => (
                      <li key={`${r.email}-${r.name}`}>{r.name}{r.jobTitle ? `, ${r.jobTitle}` : ''}{r.active ? '' : ' (inactive)'}</li>
                    ))}
                  </ul>
                </details>
              )}
            </section>
          </>
        )}
      </main>
    </>
  );
}
